import _, {get, isEqual} from 'lodash';
import {ActionType as TSActionType} from 'typesafe-actions';

import {type RunFieldsFragment} from '../../generated/graphql';
import {setInShallowClone} from '../../util/utility';
import * as RunSelectorRunsActions from '../runSelectorRuns/actions';
import * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as CustomRunColorsActions from './customRunColors/actions';
import * as CustomRunNamesActions from './customRunNames/actions';
import * as DiscussionCommentActions from './discussionComment/actions';
import * as FilterActions from './filter/actions';
import * as GroupSelectionsActions from './groupSelections/actions';
import * as GroupSelectionsActionsInternal from './groupSelections/actionsInternal';
import * as MarkdownBlockActions from './markdownBlock/actions';
import * as Normalize from './normalize';
import {StateType} from './normalizerSupport';
import * as PanelActions from './panel/actions';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import type * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import * as PanelSettingsActions from './panelSettings/actions';
import * as ReportActions from './report/actions';
import * as RunSetActions from './runSet/actions';
import {RunSetPart} from './runSet/types';
import * as SectionActions from './section/actions';
import * as SemanticLegendActions from './semanticLegend/actions';
import * as SortActions from './sort/actions';
import * as TempSelectionsActions from './tempSelections/actions';
import * as TempSelectionsActionsInternal from './tempSelections/actionsInternal';
import * as Types from './types';
import * as WorkspaceSettingsActions from './workspaceSettings/actions';

export type ActionType = TSActionType<
  | typeof Actions
  | typeof ActionsInternal
  | typeof SortActions
  | typeof FilterActions
  | typeof CustomRunColorsActions
  | typeof CustomRunNamesActions
  | typeof GroupSelectionsActions
  | typeof GroupSelectionsActionsInternal
  | typeof PanelActions
  | typeof PanelBankConfigActions
  | typeof PanelBankConfigActionsInternal
  | typeof PanelBankSectionConfigActions
  | typeof PanelSettingsActions
  | typeof SectionActions
  | typeof MarkdownBlockActions
  | typeof RunSetActions
  | typeof ReportActions
  | typeof TempSelectionsActions
  | typeof TempSelectionsActionsInternal
  | typeof DiscussionCommentActions
  | typeof WorkspaceSettingsActions
  | typeof SemanticLegendActions
  | typeof RunSelectorRunsActions
>;

interface ViewList {
  loading: boolean;
  // The metadatalist query that produced this list
  query: Types.LoadMetadataListParams;
  // Pointers to the .views field of the reducer
  viewIds: string[];
}

export type UndoRedoHistoryItem = {
  /** id is used to track whether the user made changes that can be saved */
  id: string;
  /** the action to apply on undo or redo */
  action: ActionType;
};

export interface ViewReducerState {
  // lists are the results of querying the server for lists of views
  lists: {[id: string]: ViewList};
  // any views that are loaded from the server or user created
  // (and not yet saved).
  views: {[id: string]: Types.LoadableView};
  loading: boolean;
  // normalized view specs
  parts: StateType;

  undoActions: UndoRedoHistoryItem[]; // stack of actions that undo past actions
  redoActions: UndoRedoHistoryItem[]; // stack of actions that undo future actions
}

export function deleteParts(state: ViewReducerState, ref: Types.AllPartRefs) {
  if (!Normalize.partExists(state.parts, ref)) {
    return;
  }
  const {partsWithRef} = Normalize.denormalizeWithParts(state.parts, ref);
  const subRefs = partsWithRef.map(p => p.ref);
  for (const subRef of subRefs) {
    delete state.parts[subRef.type][subRef.id];
  }
}

export function deletePartsImmutable(
  state: ViewReducerState,
  ref: Types.AllPartRefs
) {
  if (!Normalize.partExists(state.parts, ref)) {
    return state;
  }
  const {partsWithRef} = Normalize.denormalizeWithParts(state.parts, ref);
  const subRefs = partsWithRef.map(p => p.ref);
  const newParts = Object.assign({}, state.parts);
  for (const subRef of subRefs) {
    if (newParts[subRef.type] === state.parts[subRef.type]) {
      // @ts-ignore
      newParts[subRef.type] = Object.assign({}, newParts[subRef.type]);
    }
    delete newParts[subRef.type][subRef.id];
  }
  return {
    ...state,
    parts: newParts,
  };
}

export function replacePart(
  state: ViewReducerState,
  oldRef: Types.AllPartRefs,
  newRef: Types.AllPartRefs
) {
  // Normalize.addObj adds the object under a newly generated ID.
  // So move it to the requested ID.
  state.parts[oldRef.type][oldRef.id] = _.cloneDeep(
    state.parts[newRef.type][newRef.id]
  );
  delete state.parts[newRef.type][newRef.id];
}

export function replacePartImmutable(
  state: ViewReducerState,
  oldRef: Types.AllPartRefs,
  newRef: Types.AllPartRefs
): ViewReducerState {
  const newState = setInShallowClone(
    state,
    ['parts', oldRef.type, oldRef.id],
    state.parts[newRef.type][newRef.id]
  );
  delete newState.parts[oldRef.type][newRef.id];
  return newState;
}

function getFilterActionsForDeletedRefsFnInRemoveHistoryForObject(
  state: ViewReducerState,
  ref: Types.AllPartRefs
) {
  if (!Normalize.partExists(state.parts, ref)) {
    // Part doesn't exist, don't filter anything.
    return () => true;
  }
  const {partsWithRef} = Normalize.denormalizeWithParts(state.parts, ref);
  const subRefIDs = new Set(partsWithRef.map(p => p.ref.id));
  return ({action: undoAction}: UndoRedoHistoryItem) => {
    if ('payload' in undoAction && 'ref' in undoAction.payload) {
      return !subRefIDs.has(undoAction.payload.ref.id);
    }

    return true;
  };
}

export function immutableRemoveHistoryForObject(
  state: ViewReducerState, // this is a plain object
  ref: Types.AllPartRefs
): ViewReducerState {
  const filterActionsForDeletedRefsFn =
    getFilterActionsForDeletedRefsFnInRemoveHistoryForObject(state, ref);
  return {
    ...state,
    undoActions: state.undoActions.filter(filterActionsForDeletedRefsFn),
    redoActions: state.redoActions.filter(filterActionsForDeletedRefsFn),
  };
}

export function removeHistoryForObject(
  state: ViewReducerState, // this is an immer draft
  ref: Types.AllPartRefs
) {
  const filterActionsForDeletedRefsFn =
    getFilterActionsForDeletedRefsFnInRemoveHistoryForObject(state, ref);
  state.undoActions = state.undoActions.filter(filterActionsForDeletedRefsFn);
  state.redoActions = state.redoActions.filter(filterActionsForDeletedRefsFn);
}

/**
 * Updates a runSet by id by applying the function to the existing runSet, if
 * it can be found. If the value returned by the function is the same reference
 * identity as the existing runSet, the state is not updated.
 *
 * @param state
 * @param runSetId
 * @param fn
 */
export function updateRunSet(
  state: ViewReducerState,
  runSetId: string,
  fn: (runSet: RunSetPart) => RunSetPart
) {
  const runSet = get(state, ['parts', 'runSet', runSetId]);
  if (!runSet) {
    return state;
  }
  try {
    const updatedRunSet = fn(runSet);
    if (updatedRunSet === runSet) {
      // returned the same reference identity, don't update state:
      return state;
    }

    return {
      ...state,
      parts: {
        ...state.parts,
        runSet: {
          ...state.parts.runSet,
          [runSetId]: updatedRunSet,
        },
      },
    };
  } catch {
    return state;
  }
}

/**
 * Look through the a list of pinned runs (which includes the baseline run) and
 * ensure that they match the pinned/baseline runs in the provided runSet. If
 * there is a mismatch, return an updated runSet with the missing pins/baseline
 * removed.
 *
 * @param runSet
 * @param pinnedRuns
 * @return The cleaned up runSet (or the original runSet if no changes were needed)
 */
export function cleanupRunSetPinnedRuns(
  runSet: RunSetPart,
  pinnedRuns: RunFieldsFragment[] | null
) {
  if (!pinnedRuns) {
    return runSet;
  }
  const pinnedRunIds = new Set(pinnedRuns.map(r => r.id));
  const newRunSet = {...runSet};

  // Baseline run has been moved or deleted from this project, so remove
  // it from the runset baseline run:
  if (newRunSet.baselineRunId && !pinnedRunIds.has(newRunSet.baselineRunId)) {
    delete newRunSet.baselineRunId;
  }
  // Filter pinned runs to only those that still exist in the project:
  if (newRunSet.pinnedRunIds) {
    newRunSet.pinnedRunIds = newRunSet.pinnedRunIds.filter(id =>
      pinnedRunIds.has(id)
    );
  }

  if (isEqual(newRunSet, runSet)) {
    return runSet; // maintain reference equality
  }
  return newRunSet;
}
